// src/resources/dashboards/ProductEvolution/sections/SignalsSection.tsx
//
// Posto PARADO — sem nenhum abastecimento nos últimos 7 dias — é o sinal de
// churn mais forte que existe e estava ausente do score antigo, por isso ele
// abre a seção, antes do próprio score.
//
// O critério NÃO é `place.last_ping_at`: o ping só é escrito pelo pontuax-sync
// para `sync_type = 'auto-system'` (114 dos 644 postos ativos), e nos outros a
// coluna é sempre NULL — a tela anunciava "18 de 18 postos sem comunicação"
// para rede rodando normalmente. Abastecimento existe para toda integração.
//
// O risco é explicado item a item com o `reasons[]` que o backend já manda em
// texto. O painel anterior mostrava cinco sub-scores numéricos sem narrativa e
// por isso ninguém agia sobre ele: número não diz o que fazer na segunda-feira.
//
// A janela do score é FIXA (30 dias) e não segue o filtro de período — está
// escrito na tela, senão o operador compara com o que está vendo acima e acha
// que o painel se contradiz.
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from '../../shared';
import type { SectionState } from '../../shared';
import { ChainSignalsData, RISK_LABEL, SIGNAL_LABEL, TIMELINE_LIMIT } from '../types';
import {
  BackendNote, Card, ShowingCount, Stat, StatGrid, fmtDate, fmtDateTime, humanizeAgo,
} from './kit';

const HEIGHT = 640;
const PLACES_MAX_HEIGHT = 300;
const TIMELINE_MAX_HEIGHT = 280;

/** Dias sem abastecimento que caracterizam posto parado. Espelha STALLED_DAYS
 *  do backend (admin-metrics-chain.ts) — mudou lá, muda aqui. */
const STALLED_DAYS = 7;

const RISK_COLOR: Record<string, string> = {
  high: COLORS.danger,
  medium: COLORS.warning,
  low: COLORS.success,
};

/** Rótulo pt-BR das entidades que aparecem na timeline de mudanças. */
const ENTITY_LABEL: Record<string, string> = {
  station_chain: 'Rede',
  place: 'Posto',
  employee: 'Usuário',
  scoring: 'Regra de pontuação',
  gift: 'Brinde',
  drawing: 'Sorteio',
  product: 'Produto',
  discount: 'Desconto',
  client_bill: 'Fatura',
};

/** snake_case → texto legível, quando não há rótulo mapeado. */
const humanizeKey = (value: string | null | undefined) =>
  !value ? '—' : value.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

const Th = ({ children, align = 'right' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(120,105,223,0.12)', whiteSpace: 'nowrap', py: 0.75,
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted,
    position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1,
  }}>
    {children}
  </TableCell>
);

const Td = ({ children, align = 'right', bold, color }: {
  children: React.ReactNode; align?: 'left' | 'right'; bold?: boolean; color?: string;
}) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, whiteSpace: 'nowrap',
    fontSize: 12.5, fontWeight: bold ? 700 : 400, color: color ?? COLORS.ink,
  }}>
    {children}
  </TableCell>
);

const SignalsSection = ({ state }: { state: SectionState<ChainSignalsData> }) => {
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const { risk, places, summary, admin, timeline } = state.data;
  const riskColor = RISK_COLOR[risk.riskLevel] ?? COLORS.faint;
  const stalledPlaces = places.filter(p => p.stalled);
  const errorPlaces = places.filter(p => p.unresolvedErrors > 0);
  const loginAgo = humanizeAgo(admin.lastLoginAt);

  return (
    <Box>
      <SectionHeader
        icon={<MonitorHeartIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.danger}, ${COLORS.warning})`}
        title="Sinais e risco"
        subtitle="Postos parados, erros de concentrador, acesso ao admin e o porquê do score de risco"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        {/* Postos parados PRIMEIRO: é o sinal de churn mais forte da lista. */}
        <Box sx={{
          ...cardSx,
          bgcolor: stalledPlaces.length ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.05)',
          border: `1px solid ${stalledPlaces.length ? 'rgba(239,68,68,0.30)' : 'rgba(34,197,94,0.28)'}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: stalledPlaces.length ? 1.25 : 0 }}>
            <LocalGasStationIcon
              sx={{ fontSize: 24, color: stalledPlaces.length ? COLORS.danger : COLORS.success }} />
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: stalledPlaces.length ? COLORS.danger : COLORS.success }}>
                {stalledPlaces.length
                  ? `${fmtNum(stalledPlaces.length)} de ${fmtNum(summary.activePlaces)} postos parados`
                  : `Todos os ${fmtNum(summary.activePlaces)} postos ativos abasteceram nos últimos ${STALLED_DAYS} dias`}
              </Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                Sem nenhum abastecimento nos últimos {STALLED_DAYS} dias · é o sinal de churn mais forte do painel
              </Typography>
            </Box>
          </Box>
          {stalledPlaces.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {stalledPlaces.map(p => (
                <Box key={p.placeId} sx={{
                  px: 1.25, py: 0.75, borderRadius: 2.5, bgcolor: '#fff', border: '1px solid rgba(239,68,68,0.30)',
                }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: COLORS.ink }}>{p.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
                    {p.lastFillinDate
                      ? `último abastecimento ${fmtDate(p.lastFillinDate)}`
                      : 'sem abastecimento no período'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.15fr 1fr' }, gap: 1.75 }}>
          <Card title="Por que esta rede está nesse risco" hint={`Janela fixa de ${fmtNum(risk.inputs.windowDays)} dias — não segue o filtro de período acima`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                bgcolor: riskColor, color: '#fff',
              }}>
                <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{fmtNum(risk.score)}</Typography>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>score</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: riskColor }}>
                  Risco {(RISK_LABEL[risk.riskLevel] ?? risk.riskLevel).toLowerCase()}
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                  {risk.reasons.length
                    ? `${fmtNum(risk.reasons.length)} sinal${risk.reasons.length === 1 ? '' : 'is'} acionado${risk.reasons.length === 1 ? '' : 's'}`
                    : 'Nenhum sinal de risco acionado'}
                </Typography>
              </Box>
            </Box>

            {!risk.reasons.length ? (
              <EmptyHint text="Nenhum sinal de risco acionado nesta janela." />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                {risk.reasons.map((reason, i) => (
                  <Box key={`${reason.signal}-${i}`} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.25, py: 0.9, borderRadius: 2.5,
                    bgcolor: 'rgba(148,163,184,0.08)', borderLeft: `3px solid ${riskColor}`,
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted }}>
                        {SIGNAL_LABEL[reason.signal] ?? reason.signal}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.5 }}>{reason.text}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: riskColor, whiteSpace: 'nowrap' }}>
                      +{fmtNum(reason.weight)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <BackendNote>
              Insumos da janela: {fmtNum(risk.inputs.fillins30d)} abastecimentos (antes {fmtNum(risk.inputs.fillinsPrev30d)}) ·
              {' '}{fmtNum(risk.inputs.activeCustomers30d)} clientes ativos (antes {fmtNum(risk.inputs.activeCustomersPrev30d)}) ·
              {' '}{fmtNum(risk.inputs.newCustomers30d)} novos · {fmtNum(risk.inputs.activeModules)} módulos em uso
              {' '}(antes {fmtNum(risk.inputs.activeModulesPrev)}) ·
              {' '}{risk.inputs.avgRating === null
                ? 'sem avaliação'
                : `nota média ${risk.inputs.avgRating.toFixed(1)} em ${fmtNum(risk.inputs.ratings)} avaliações`}.
            </BackendNote>
          </Card>

          <Card title="Resumo operacional" hint="Estado dos postos, financeiro e acesso ao admin">
            <StatGrid min={132}>
              <Stat label="Postos ativos" value={fmtNum(summary.activePlaces)} />
              <Stat label={`Sem abastecer ${STALLED_DAYS}d`} value={fmtNum(summary.noFillinPlaces)}
                color={summary.noFillinPlaces ? COLORS.danger : COLORS.success} />
              <Stat label="Com erro em aberto" value={fmtNum(summary.errorPlaces)}
                color={summary.errorPlaces ? COLORS.warning : COLORS.success}
                sub="erros dos últimos 7 dias" />
              <Stat label="Considerados parados" value={fmtNum(summary.stalledPlaces)}
                color={summary.stalledPlaces ? COLORS.danger : COLORS.success}
                hint="O maior entre 'sem abastecer' e 'com erro', limitado ao total de postos ativos: somar contaria em dobro o posto que parou de produzir E está com erro." />
              <Stat label="Faturas vencidas" value={fmtNum(summary.overdueBills)}
                color={summary.overdueBills ? COLORS.danger : COLORS.success} sub={fmtBrl(summary.overdueValue)} />
            </StatGrid>

            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid rgba(148,163,184,0.2)' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: COLORS.faint }}>
                Último acesso ao admin
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: admin.lastLoginAt ? COLORS.ink : COLORS.warning }}>
                {admin.lastLoginAt ? `${fmtDateTime(admin.lastLoginAt)} · ${loginAgo}` : 'Nenhum acesso registrado'}
              </Typography>
              <BackendNote>
                {admin.loginTrackingStartedAt
                  ? `O rastreio de login começou em ${fmtDate(admin.loginTrackingStartedAt)} — antes disso não há histórico.`
                  : 'O rastreio de login ainda não tem histórico no sistema.'}
              </BackendNote>
            </Box>
          </Card>
        </Box>

        <Card
          title="Postos: último abastecimento e erros"
          hint="Ordenado pelo abastecimento mais antigo — quem está parado há mais tempo aparece primeiro"
          right={<ShowingCount shown={places.length} total={places.length} noun="posto" />}
        >
          {!places.length ? (
            <EmptyHint text="Nenhum posto ativo nesta rede." />
          ) : (
            <Box sx={{ maxHeight: PLACES_MAX_HEIGHT, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
                <TableHead>
                  <TableRow>
                    <Th align="left">Posto</Th>
                    <Th align="left">Situação</Th>
                    <Th align="left">Último abastecimento</Th>
                    <Th>Erros em aberto</Th>
                    <Th align="left">Último erro</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {places.map(p => (
                    <TableRow key={p.placeId} hover sx={{ bgcolor: p.stalled ? 'rgba(239,68,68,0.05)' : undefined }}>
                      <Td align="left" bold>{p.name}</Td>
                      <Td align="left" bold color={p.stalled ? COLORS.danger : COLORS.success}>
                        {p.stalled ? 'parado' : 'abastecendo'}
                      </Td>
                      <Td align="left">
                        {p.lastFillinDate ? fmtDate(p.lastFillinDate) : 'sem abastecimento no período'}
                      </Td>
                      <Td bold color={p.unresolvedErrors ? COLORS.warning : undefined}>{fmtNum(p.unresolvedErrors)}</Td>
                      <Td align="left">{p.lastErrorAt ? fmtDateTime(p.lastErrorAt) : '—'}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {errorPlaces.length > 0 && (
            <BackendNote tone="warning">
              {fmtNum(errorPlaces.length)} posto{errorPlaces.length === 1 ? '' : 's'} com erro de concentrador não resolvido
              {' '}nos últimos 7 dias.
            </BackendNote>
          )}
        </Card>

        {/* O backend corta em LIMIT 20 e não devolve o total, então o recorte é
            declarado em texto em vez de fingir uma contagem que não existe. */}
        <Card
          title="Mudanças recentes no cadastro"
          hint={`Alterações registradas no histórico da rede · no máximo as ${TIMELINE_LIMIT} mais recentes`}
        >
          {!timeline.length ? (
            <EmptyHint text="Nenhuma mudança registrada para esta rede." />
          ) : (
            <>
              <Box sx={{ maxHeight: TIMELINE_MAX_HEIGHT, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <Th align="left">Quando</Th>
                      <Th align="left">Onde</Th>
                      <Th align="left">Campo</Th>
                      <Th align="left">De</Th>
                      <Th align="left">Para</Th>
                      <Th align="left">Origem</Th>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeline.map(entry => (
                      <TableRow key={entry.id} hover>
                        <Td align="left">{fmtDateTime(entry.changedAt)}</Td>
                        <Td align="left" bold>{ENTITY_LABEL[entry.entityType] ?? humanizeKey(entry.entityType)}</Td>
                        <Td align="left">{humanizeKey(entry.fieldName)}</Td>
                        <Td align="left" color={COLORS.muted}>{entry.oldValue ?? '—'}</Td>
                        <Td align="left" bold>{entry.newValue ?? '—'}</Td>
                        <Td align="left" color={COLORS.faint}>{humanizeKey(entry.origin)}</Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {timeline.length === TIMELINE_LIMIT && (
                <BackendNote tone="warning">
                  Mostrando as {fmtNum(TIMELINE_LIMIT)} mudanças mais recentes — pode haver mais histórico além destas.
                </BackendNote>
              )}
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default SignalsSection;
